import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { TrendingUp } from "lucide-react";
import { StoryCoverTile } from "@/components/ui/story-cover-tile";
import { Carousel } from "@/components/ui/carousel";
import { formatWordCount, formatRelativeTime } from "@/lib/utils";

// ─── Types ───────────────────────────────────────────────

interface TrendingStory {
  id: string;
  title: string;
  slug: string;
  coverUrl: string | null;
  chapterCount: number;
  totalWordCount: number;
  authorName: string;
  updatedAt: Date | null;
}

// ─── Component ───────────────────────────────────────────

interface TrendingCarouselProps {
  stories: TrendingStory[];
}

/**
 * A horizontal scrollable carousel of trending stories.
 * Only renders if there are stories to show.
 */
export function TrendingCarousel(t0) {
  const $ = _c(5);
  const {
    stories
  } = t0;
  if (stories.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TrendingUp className="size-6 text-accent" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== stories) {
    t2 = stories.map(_temp);
    $[1] = stories;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Carousel title="New & Trending" icon={t1}>{t2}</Carousel>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp(story, index) {
  return <Link key={story.id} href={`/story/${story.slug}`} className="group flex-shrink-0 w-64 md:w-72 snap-start flex flex-col"><StoryCoverTile coverUrl={story.coverUrl} title={story.title} sizes="(min-width: 768px) 288px, 256px" priority={index === 0} badge={{
      label: "New",
      position: "left",
      tone: "accent"
    }} shadow={false} className="mb-3"><div className="absolute bottom-0 inset-x-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent items-end p-4 hidden dark:flex"><div className="flex items-center gap-2 text-white text-xs"><span className="opacity-80">{story.chapterCount} {story.chapterCount === 1 ? "ch" : "chs"}</span><span className="opacity-50">•</span><span className="opacity-80">{formatWordCount(story.totalWordCount)} words</span></div></div><div className="absolute bottom-0 inset-x-0 h-3/5 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex flex-col justify-end p-4 dark:hidden"><h4 className="font-bold text-white line-clamp-2 drop-shadow-md mb-1 leading-snug">{story.title}</h4><p className="text-white/80 text-[11px] drop-shadow-md mb-1.5 line-clamp-1">by {story.authorName} • Updated {formatRelativeTime(story.updatedAt)}</p><div className="flex items-center gap-2 text-white/90 text-xs drop-shadow-md"><span>{story.chapterCount} {story.chapterCount === 1 ? "ch" : "chs"}</span><span className="opacity-50">•</span><span>{formatWordCount(story.totalWordCount)} words</span></div></div></StoryCoverTile><div className="hidden dark:block"><h4 className="font-bold text-slate-900 dark:text-white truncate">{story.title}</h4><p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">by {story.authorName} • Updated {formatRelativeTime(story.updatedAt)}</p></div></Link>;
}